import { defineBlock, useSmartProp, useInspectorControl, smartProps, useEditor } from '@smartfire/js-block-engine';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, Button, AppBar, Toolbar, Container, Link } from '@mui/material';
// hooks
import useOffSetTop from '../hooks/useOffSetTop';
import useResponsive from '../hooks/useResponsive';
// utils
import cssStyles from '../utils/cssStyles';

import { HEADER } from '../config';

// components
import Logo from '../components/Logo';
//
import MenuDesktop from './MenuDesktop';
import MenuMobile from './MenuMobile';

import Iconify from '../components/Iconify';

import MinimalWrapper from '../minimal-wrapper/block';
import { EditableText } from '@smartfire/components';
import { PanelBody, PanelRow, TextControl } from '@wordpress/components';
import typography from '../theme/typography';

// ----------------------------------------------------------------------

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
    height: HEADER.MOBILE_HEIGHT,
    transition: theme.transitions.create(['height', 'background-color'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shorter,
    }),
    [theme.breakpoints.up('md')]: {
        height: HEADER.MAIN_DESKTOP_HEIGHT,
    },
}));

const ToolbarShadowStyle = styled('div')(({ theme }) => ({
    left: 0,
    right: 0,
    bottom: 0,
    height: 24,
    zIndex: -1,
    margin: 'auto',
    borderRadius: '50%',
    position: 'absolute',
    width: `calc(100% - 48px)`,
    boxShadow: theme.customShadows.z8,
}));

// ----------------------------------------------------------------------

function MainHeader({ user, menu, languages, currentLang }) {
    const isOffset = useOffSetTop(HEADER.MAIN_DESKTOP_HEIGHT);
    const { pathname } = useLocation();
    const theme = useTheme();
    const { isEditing } = useEditor();

    const isHome = pathname === '/' || pathname === '/fr/';
    const rootPath = currentLang === 'fr' ? '/fr/' : '/';

    const showLanguageSwitcherLogout = useMediaQuery('(min-width:350px)');
    const showLanguageSwitcherLogged = useMediaQuery('(min-width:400px)');
    const showLanguageSwitcher = user ? showLanguageSwitcherLogged : showLanguageSwitcherLogout;
    const showFullCta = useMediaQuery('(min-width:1050px)');

    const isSmallMenu = useMediaQuery('(max-width:990px)');
    const isDesktop = useResponsive('up', 'md');
    const isMobile = useResponsive('down', 'sm');

    let redirectUrl = pathname;
    if (redirectUrl[0] === '/') redirectUrl = redirectUrl.substring(1);
    if (redirectUrl[redirectUrl.length - 1] === '/') redirectUrl = redirectUrl.substring(0, redirectUrl.length - 1);

    const menuList = menu
        ? Object.values(menu)
        : [
              { title: 'Menu Admin 1', child: [{ title: 'Example url' }] },
              { title: 'Menu Admin 2', child: [{ title: 'Example url' }] },
              { title: 'Menu Admin 3', child: [{ title: 'Example url' }] },
          ];

    return (
        <AppBar sx={{ boxShadow: 0, bgcolor: 'transparent' }} position={isEditing ? 'static' : 'fixed'}>
            <ToolbarStyle
                disableGutters
                sx={{
                    ...(isOffset && {
                        ...cssStyles(theme).bgBlur(),
                        height: { md: HEADER.MAIN_DESKTOP_HEIGHT - 16 },
                    }),
                }}
            >
                <Container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {/*<Label color="info" sx={{ ml: 1 }}>
                        WPBlast
                </Label>*/}
                    <Link
                        href={rootPath}
                        sx={{
                            ...typography.logo,
                            color: isOffset || !isHome ? theme.palette.grey[700] : theme.palette.grey[300],
                            fontSize: isMobile ? 15 : 20,
                        }}
                        underline="none"
                    >
                        <Logo
                            disabledLink={true}
                            sx={{
                                display: 'inline-block',
                                verticalAlign: 'middle',
                                marginRight: '16px',
                                marginTop: '-3px',
                            }}
                        />
                        WP BLAST
                    </Link>

                    <Box sx={{ flexGrow: 1 }} />

                    {isDesktop && <MenuDesktop isOffset={isOffset} isHome={isHome} navConfig={menuList} />}

                    {user ? (
                        <>
                            {isDesktop && showFullCta ? (
                                <Button
                                    variant="contained"
                                    href={
                                        rootPath +
                                        (user.customer.plans.length > 0
                                            ? '?manage-subscription&redirect_url=' + redirectUrl
                                            : 'pricing')
                                    }
                                >
                                    <EditableText
                                        smartProp={user.customer.plans.length > 0 ? 'ctaConnected' : 'ctaNonConnected'}
                                    />
                                </Button>
                            ) : (
                                <Link
                                    href={
                                        rootPath +
                                        (user.customer.plans.length > 0
                                            ? '?manage-subscription&redirect_url=' + redirectUrl
                                            : 'pricing')
                                    }
                                >
                                    <Iconify
                                        icon={'mdi:account'}
                                        sx={{
                                            ml: isSmallMenu ? 1.5 : 2.5,
                                            width: 25,
                                            height: 25,
                                            verticalAlign: 'middle',
                                            transition: 'opacity 0.3s',
                                            opacity: 1,
                                            '&:hover': { opacity: '0.5' },
                                        }}
                                    />
                                </Link>
                            )}
                            <Link href="?logout">
                                <Iconify
                                    icon={'fe:logout'}
                                    sx={{
                                        ml: isSmallMenu ? 1.5 : 2.5,
                                        width: 25,
                                        height: 25,
                                        verticalAlign: 'middle',
                                        transition: 'opacity 0.3s',
                                        opacity: 1,
                                        '&:hover': { opacity: '0.5' },
                                    }}
                                />
                            </Link>
                        </>
                    ) : (
                        <>
                            {isDesktop && showFullCta ? (
                                <Button variant="contained" href={rootPath + 'installation/'}>
                                    <EditableText smartProp="ctaNonConnected" />
                                </Button>
                            ) : null}
                            {isDesktop ? (
                                <Link href={rootPath + 'login/'}>
                                    <Iconify
                                        icon={'mdi:account'}
                                        sx={{
                                            ml: isSmallMenu ? 1.5 : 2.5,
                                            width: 25,
                                            height: 25,
                                            verticalAlign: 'middle',
                                            transition: 'opacity 0.3s',
                                            opacity: 1,
                                            '&:hover': { opacity: '0.5' },
                                        }}
                                    />
                                </Link>
                            ) : null}
                        </>
                    )}
                    {languages && showLanguageSwitcher && (
                        <>
                            {Object.values(languages).map((language) => (
                                <Link key={language.id} href={language.url}>
                                    <Iconify
                                        icon={language.code === 'fr' ? 'cif:fr' : 'cif:us'}
                                        sx={{
                                            ml: isSmallMenu ? 1.5 : 2.5,
                                            width: 25,
                                            height: 25,
                                            verticalAlign: 'middle',
                                            transition: 'opacity 0.3s',
                                            opacity: 1,
                                            '&:hover': { opacity: '0.5' },
                                        }}
                                    />
                                </Link>
                            ))}
                        </>
                    )}

                    {!isDesktop && (
                        <MenuMobile
                            isOffset={isOffset}
                            isHome={isHome}
                            navConfig={menuList}
                            user={user}
                            languages={languages}
                            showLanguageSwitcher={showLanguageSwitcher}
                            rootPath={rootPath}
                        />
                    )}
                </Container>
            </ToolbarStyle>

            {isOffset && !isEditing ? <ToolbarShadowStyle /> : null}
        </AppBar>
    );
}

function Inspector() {
    const [ctaConnected, setCtaConnected] = useSmartProp('ctaConnected');
    const [ctaNonConnected, setCtaNonConnected] = useSmartProp('ctaNonConnected');

    return (
        <>
            <PanelBody title="Settings" initialOpen={true}>
                <PanelRow>
                    <TextControl label="CTA connected" value={ctaConnected} onChange={setCtaConnected} />
                </PanelRow>
                <PanelRow>
                    <TextControl label="CTA non connected" value={ctaNonConnected} onChange={setCtaNonConnected} />
                </PanelRow>
            </PanelBody>
        </>
    );
}

function Block(props) {
    useInspectorControl(Inspector);

    return (
        <MinimalWrapper>
            <MainHeader {...props} />
        </MinimalWrapper>
    );
}

export default defineBlock({
    Block,
    smartProps: {
        ctaConnected: smartProps.string.withDefault('My Account'),
        ctaNonConnected: smartProps.string.withDefault('Get started'),
    },
    setup: {},
});
